import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Shield } from "lucide-react";
import { publicRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { toast } from "sonner";
import { loginSuccess } from "@/store/slices/authSlice";
import { UserRole } from "@/interfaces/profile";
import { fetchSettings } from "@/store/actions/settingsActions";
import { useAppDispatch } from "@/store/hooks";
import { formatTime } from "@/utils";

const TwoFALoginPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  console.log("STATE", state);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [isResendLoading, setisResendLoading] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const userId = state?.payload?.userId;
  const email = state?.payload?.email;

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const initializeTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const saved = sessionStorage.getItem("twoFactorOtp");

    if (!saved) return;

    const { issuedAt, cooldown } = JSON.parse(saved);

    const expiry = new Date(issuedAt).getTime() + cooldown * 1000;

    const updateTimer = () => {
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));

      setTimer(remaining);

      if (remaining <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        sessionStorage.removeItem("twoFactorOtp");
      }
    };

    updateTimer();

    timerRef.current = setInterval(updateTimer, 1000);
  };

  useEffect(() => {
    if (!userId || !email) {
      navigate("/login", { replace: true });
    }
  }, [userId, email, navigate]);

  useEffect(() => {
    initializeTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");

    setisLoading(true);
    try {
      const res = await publicRequest.post("/auth/login/twofa", {
        userId,
        email,
        otp: otpString,
        trustDevice,
      });

      const payload = res.data;
      const user = payload.user;

      dispatch(loginSuccess(user));
      dispatch(fetchSettings());

      sessionStorage.removeItem("twoFactorOtp");

      if (payload.code === "TRANSACTION_PASSWORD_REQUIRED") {
        toast.info("Please create your transaction password to continue.");

        navigate("/setup/transaction-password", {
          replace: true,
          state: {
            required: true,
          },
        });

        return;
      }

      toast.success(payload.message || "Login successful");

      navigate(user.role === UserRole.SUPER ? "/admin" : "/dashboard", {
        replace: true,
      });

      setisLoading(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setisLoading(false);
    }
  };

  const handleResend = async () => {
    setisResendLoading(true);

    try {
      const res = await publicRequest.put("/auth/email/verify/otp/two-fa", {
        email,
      });

      const cooldown = res.data?.otp?.resendCooldownSeconds ?? 60;

      const issuedAt = res.data?.otp?.issuedAt ?? new Date().toISOString();

      sessionStorage.setItem(
        "twoFactorOtp",
        JSON.stringify({
          email,
          issuedAt,
          cooldown,
        }),
      );

      initializeTimer();

      setOtp(["", "", "", "", "", ""]);

      toast.success("OTP resent successfully");
      setisResendLoading(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setisResendLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full sm:max-w-md mx-auto">
        {/* HEADER */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-black" />
          </div>

          <h1 className="text-2xl font-bold text-foreground mb-2">
            Two-Factor Authentication
          </h1>

          <p className="text-muted-foreground">
            Enter the 6-digit code sent to {email}
          </p>
        </div>

        {/* CARD */}
        <div className="nex-card p-8">
          <div className="space-y-6">
            {/* OTP INPUTS */}
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-14 text-center text-xl font-bold bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                />
              ))}
            </div>

            {/* TRUST DEVICE */}
            {/* <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-sm text-muted-foreground">
                Trust this device
              </label>
            </div> */}

            {/* VERIFY BUTTON */}
            <button
              onClick={handleVerify}
              disabled={otp.some((d) => !d) || isLoading || isResendLoading}
              className="w-full py-3 bg-[#F0B90B] text-black font-semibold rounded-lg hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? "Verifying..." : "Verify & Login"}
              <ArrowRight className="w-5 h-5" />
            </button>

            {/* RESEND */}
            <div className="text-center">
              {timer > 0 ? (
                <p className="text-muted-foreground">
                  Resend code in {formatTime(timer)}
                </p>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={isResendLoading}
                  className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResendLoading ? "Resending..." : "Resend OTP"}
                </button>
              )}
            </div>
          </div>

          {/* FOOTER */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFALoginPage;
